import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionCreateDocumentFromTemplateDenormalized,
  AutomationActionDenormalized,
  AutomationActionStatus,
  AutomationActionType,
  EmployeeAppRerouterHints,
  EventChannel,
  ExperienceEventType,
  ExperienceStatus,
  IExperience,
  IExperienceTemplate,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY,
  SubTaskStatus,
  SubTaskType,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { ErroDataType } from '@dadohr/shared/domainTypes/instance';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grow,
  Link,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { TFunction } from 'i18next';
import { compact, groupBy, isEqual } from 'lodash';
import { AlertCircle, ChevronDown, ExternalLink } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { WithShape } from '../../../../components/Charts/Util';
import { ColoredChipDark } from '../../../../components/ColoredChip';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../components/DialogActionsWithSlots';
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog';
import { InfoBox, InfoBoxWithAction } from '../../../../components/InfoBox';
import { LinkExternal } from '../../../../components/LinkExternal';
import { Loader } from '../../../../components/Loader';
import { SomethingWentWrong } from '../../../../components/SomethingWentWrong';
import { TabPanel } from '../../../../components/Tabs';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { advanceStickyValue } from '../../../../hooks/stickyValue';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { captureException } from '../../../../sentry';
import { useCurrentUser } from '../../../../services/currentUser';
import { useNewUx } from '../../../../services/newUx';
import { ParticipantDialogNewUx } from './ParticipantDialogNewUx';
import {
  archiveInstance,
  deleteArchivedInstance,
  DenormalizationResources,
  getParticipantActor,
  isInstanceAutoArchived,
  openSubTask,
  sendManualReminder,
  toStandaloneAppUrl,
  updateInstanceSchedules,
  useActorsForExperience,
  useAdditionalDenormalizationResources,
  useExperienceInstanceById
} from '../../../../services/experiences/instance';
import {
  getMessageTemplateById,
  getTaskTemplateById,
  humanReadableActorName,
  useExperienceTemplateById
} from '../../../../services/experiences/template';
import {
  combineLoadingValues3,
  setDoc,
  updateDataInDoc,
  updateDoc,
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../../services/firebase';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { LightMdToJsx } from '../../../../services/formatter';
import { getOrg } from '../../../../services/orgs';
import { _mapFn } from '../../../../services/participatDialog/participantDialogHelpers';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { InterpolationDefaultOptions } from '../../../../services/set/interpolation';
import {
  formatDatePrecise,
  fromMoment,
  now,
  ONE_DAY,
  toMoment,
  tsIsAfter
} from '../../../../services/time';
import { toFullName, useUserById } from '../../../../services/user';
import { getDocuSignWebhookListenerByInstance } from '../../../Org/pages/Integrations/integrations/docusign/service';
import { UserTaskTimeline } from '../../../Users/components/UserTaskTimeline';
import {
  ActorDescriptor,
  useActorDescriptorsFromTemplate
} from '../../components/Actors';
import { getAnchorFields, useAnchorFields } from '../../components/Anchors';
import { useActionDefinition } from '../../components/Builders/AutomationBuilder/actionHelpers';
import { ExperienceLog } from '../../components/ExperienceLog';
import { InstanceStatusChip } from '../../components/InstanceStatusChip';
import { UpcomingSchedules } from '../../components/UpcomingSchedules';
import { getItemDisplayName, resolveExpTitle } from '../../helpers/adminName';
import {
  CCErrorData,
  ExperienceProblemData,
  SentItemErrorData
} from './InstanceProblems';
import {
  BackgroundCheckSuggestedAction,
  getBackgroundCheckDetail
} from './BackgroundCheckInstanceProblems';
import { getAdditionalInfoText } from './getAdditionalInfoText';
import { legacyCopy } from './issues/legacyCopy';
import { buildAutomationIssue, buildIssue } from './issues/types';
import { getChangeEvents, getFieldOverridesFromChanges } from './modifications';
import { CustomizeDates } from './ParticipantDialogComponents/CustomizeDates';
import { TimelineItemStatusList } from './TimelineItemStatusList';

const CA_TEST = false;
const USE_MANUAL_REMINDERS = false;

/*
@guide Create a new automation action - 4.5 Add Suggested Actions for an error
*/
export const SuggestedActionForError = ({
  action: a,
  instance
}: {
  action: AutomationActionDenormalized;
  instance: Doc<IExperience>;
}) => {
  const { t } = useUnsafeTranslation();
  const [, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  const { org } = useCurrentUser();
  const { ROUTES } = useRoutes();

  const issue = buildAutomationIssue(a, instance, {
    t,
    customUserFields: org.customUserFields,
    onOpenParticipant: setDialogInstance,
    routes: ROUTES,
    getAnchorFields
  });
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

/*
@guide Create a new automation action - 4.4 Add Error details

Here we parse errorData (if available) and render this information in a human readable way.
*/
export const getActionErrorDetailsText = ({
  action: a,
  customUserFields,
  supportingActors,
  t
}: {
  action: AutomationActionDenormalized;
  customUserFields?: CustomUserField[];
  supportingActors?: IExperienceTemplate['supportingActors'];
  t: UnsafeTFunction;
}): string | null => {
  const issue = buildAutomationIssue(a, undefined, {
    t,
    customUserFields,
    supportingActors,
    humanReadableActorName,
    getAnchorFields
  });
  const result = legacyCopy[issue.kind].whatCausedIt([issue]);
  return typeof result === 'string' ? result : null;
};

export const ActionErrorDetails = ({
  action: a,
  supportingActors
}: {
  action: AutomationActionDenormalized;
  supportingActors?: IExperienceTemplate['supportingActors'];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const text = getActionErrorDetailsText({
    action: a,
    supportingActors,
    customUserFields: org.customUserFields,
    t
  });
  return text ? <LightMdToJsx>{text}</LightMdToJsx> : null;
};

export const SuggestedActionForMissingData = ({
  errData
}: {
  errData: ExperienceProblemData;
}) => {
  const { t } = useUnsafeTranslation();
  const [, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  const issue = buildIssue(errData, { t, onOpenParticipant: setDialogInstance });
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

export const SuggestedActionForSentItem = ({
  errData,
  supportingActors
}: {
  errData: SentItemErrorData;
  supportingActors: { [actorKey: string]: ISupportingActor };
}) => {
  const { t } = useUnsafeTranslation();
  const issue = buildIssue(errData, { t, supportingActors });
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

export const getSentItemErrorDetailsText = ({
  sentItem,
  supportingActors,
  t
}: {
  sentItem: SentItemErrorData;
  supportingActors: { [actorKey: string]: ISupportingActor };
  t: UnsafeTFunction;
}) => {
  const issue = buildIssue(sentItem, { t, supportingActors });
  const result = legacyCopy[issue.kind].whatCausedIt([issue]);
  return typeof result === 'string' ? result : null;
};

export const SentItemErrorDetails = ({
  sentItem,
  supportingActors
}: {
  sentItem: SentItemErrorData;
  supportingActors: { [actorKey: string]: ISupportingActor };
}) => {
  const { t } = useUnsafeTranslation();
  const text = getSentItemErrorDetailsText({ sentItem, supportingActors, t });
  return text ? <LightMdToJsx>{text}</LightMdToJsx> : null;
};

export const getInstanceSubTaskErrorDetails = ({
  errData,
  supportingActors,
  t
}: {
  errData: ExperienceProblemData;
  supportingActors: { [actorKey: string]: ISupportingActor };
  t: UnsafeTFunction;
}) => {
  const issue = buildIssue(errData, {
    t,
    supportingActors,
    humanReadableActorName,
    getBackgroundCheckDetail
  });
  const result = legacyCopy[issue.kind].whatCausedIt([issue]);
  return typeof result === 'string' ? result : null;
};

export const InstanceSubtaskErrorDetails = ({
  errData,
  supportingActors
}: {
  errData: ExperienceProblemData;
  supportingActors: { [actorKey: string]: ISupportingActor };
}) => {
  const { t } = useUnsafeTranslation();
  const text = getInstanceSubTaskErrorDetails({ errData, supportingActors, t });
  return text ? <LightMdToJsx>{text}</LightMdToJsx> : null;
};

export const SuggestedActionForInstanceSubtaskError = ({
  errData,
  supportingActors
}: {
  errData: ExperienceProblemData;
  supportingActors: { [actorKey: string]: ISupportingActor };
}) => {
  const { t } = useUnsafeTranslation();
  const [, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  const issue = buildIssue(errData, {
    t,
    supportingActors,
    onOpenParticipant: setDialogInstance,
    humanReadableActorName,
    BackgroundCheckSuggestedAction
  });
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

export const SuggestedActionForCCError = ({
  errData
}: {
  errData: CCErrorData;
}) => {
  const { t } = useUnsafeTranslation();
  const [, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  const issue = buildIssue(errData, { t, onOpenParticipant: setDialogInstance });
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

export const SuggestedActionForAssigneeBounce = () => {
  const { t } = useUnsafeTranslation();
  const issue = buildIssue(
    { type: 'ASSIGNEE_BOUNCE' } as ExperienceProblemData,
    { t }
  );
  return legacyCopy[issue.kind].howToFixIt([issue]);
};

const CreatDocInfoInnerComponent = ({
  action,
  t
}: {
  action: AutomationActionCreateDocumentFromTemplateDenormalized;
  t: TFunction;
}) => {
  return action.status === AutomationActionStatus.DONE ? (
    <>
      <Link href={action.createdDocPublicUrl} rel="noone" target="_blank">
        {action.fileNameFormat}
      </Link>{' '}
      {t('created')}
    </>
  ) : (
    <>
      {t(`Create '{{filename}}'`, {
        filename: action.fileNameFormat
      })}
    </>
  );
};
const AdditionalInfoComponent = ({
  small,
  action,
  mode,
  t,
  wrap
}: {
  small?: boolean;
  action: AutomationActionDenormalized;
  t: TFunction;
  mode?: 'light' | 'normal';
  wrap?: boolean;
}) => {
  const infoText = getAdditionalInfoText(action, t);
  if (
    !infoText &&
    action.type !== AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE
  ) {
    return null;
  }
  return (
    <Typography
      variant={small ? 'caption' : 'body1'}
      style={wrap ? CLAMP_3_LINES_STYLES : ELLIPSIS_STYLES}
    >
      <ActionWithIconAdditionalInfo mode={mode}>
        {action.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE ? (
          <CreatDocInfoInnerComponent action={action} t={t} />
        ) : (
          <>{infoText}</>
        )}
      </ActionWithIconAdditionalInfo>
    </Typography>
  );
};

export const getMissingDataErrorDetailsText = ({
  errData,
  t
}: {
  errData: ExperienceProblemData;
  t: UnsafeTFunction;
}) => {
  const issue = buildIssue(errData, { t });
  const result = legacyCopy[issue.kind].whatCausedIt([issue]);
  return typeof result === 'string' ? result : null;
};

export const MissingDataErrorDetails = ({
  errData
}: {
  errData: ExperienceProblemData;
}) => {
  const { t } = useUnsafeTranslation();
  const text = getMissingDataErrorDetailsText({ errData, t });
  return text ? <LightMdToJsx>{text}</LightMdToJsx> : null;
};

export const BlockElementSummaryContainer = styled('div')<{
  mode?: 'light' | 'normal';
}>((p) => ({
  borderRadius: p.theme.custom.border.radius(),
  backgroundColor: p.theme.custom.colors.gray.w200,
  color: p.mode === 'light' ? p.theme.custom.colors.gray.w700 : undefined,
  display: 'flex',
  gap: p.theme.spacing(0.5),
  alignItems: 'center',
  padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(1)}px`
}));

const ActionWithIconAdditionalInfo = styled('div')<{
  mode?: 'light' | 'normal';
}>((p) => ({
  color: p.mode === 'light' ? p.theme.custom.colors.gray.w500 : undefined
}));
const ELLIPSIS_STYLES: React.CSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};
/** Matches the participant dialog sidebar's subtask-name clamp (SubtaskLabel). */
const CLAMP_3_LINES_STYLES: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};
const ActionWithIconContainer = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(0, 1fr)',
  gridColumnGap: p.theme.spacing(0.5),
  alignItems: 'center'
}));
export const ActionWithIcon = ({
  action,
  mode,
  small,
  wrap
}: {
  action: AutomationActionDenormalized;
  mode?: 'light' | 'normal';
  small?: boolean;
  /** Lets the action's additional-info text wrap onto multiple lines instead of
   * truncating with an ellipsis — for surfaces (e.g. a fixed-width sidebar row) that
   * want the full text visible rather than clipped. */
  wrap?: boolean;
}) => {
  const def = useActionDefinition(action);
  const { t } = useUnsafeTranslation();

  const iconSize = small ? 14 : 16;

  return (
    <ActionWithIconContainer>
      <Typography variant={small ? 'caption' : 'body1'}>
        <BlockElementSummaryContainer mode={mode}>
          {def.iconSrc && (
            <img
              alt={''}
              color={
                def.type === AutomationActionType.HTTP_POST
                  ? 'green'
                  : undefined
              }
              height={iconSize}
              width={iconSize}
              src={def.iconSrc}
            />
          )}
          <div> {def.shortLabel} </div>
        </BlockElementSummaryContainer>
      </Typography>
      <AdditionalInfoComponent
        action={action}
        small={small}
        mode={mode}
        t={t}
        wrap={wrap}
      />
    </ActionWithIconContainer>
  );
};

export const getIssueDetailsText = (
  d: ExperienceProblemData,
  customUserFields: CustomUserField[],
  t: UnsafeTFunction,
  opts: { stripFormattingFragments?: boolean } = {}
) => {
  const supportingActors = (d as { tpl?: { data: { supportingActors?: IExperienceTemplate['supportingActors'] } } })
    .tpl?.data?.supportingActors;
  const issue = buildIssue(d, {
    t,
    customUserFields,
    supportingActors,
    humanReadableActorName,
    getAnchorFields,
    getBackgroundCheckDetail
  });
  const result = legacyCopy[issue.kind].whatCausedIt([issue]);
  const text = typeof result === 'string' ? result : null;
  return text && opts.stripFormattingFragments
    ? text.replace(new RegExp(/\*/, 'g'), '')
    : text;
};

export const getIssueTypeForTracking = (d: ExperienceProblemData) => {
  if (d.type === 'SENT_ITEM') {
    const { msgId, taskIds = [] } = d.message.scope || {};
    return compact([msgId && 'MESSAGE', ...taskIds.map(() => 'TASK')]).join(
      ', '
    );
  }
  return d.type;
};

export const getIssueItemIdsForTracking = (d: ExperienceProblemData) => {
  if (d.type === 'AUTOMATION') {
    return d.automationTpl.id;
  }
  if (d.type === 'SENT_ITEM') {
    const { msgId, taskIds = [] } = d.message.scope || {};
    return compact([msgId, ...taskIds]).join(', ');
  }
  if (d.type === 'MISSING_ACTOR') {
    return d.actor.key;
  }
  if (d.type === 'MISSING_VAR') {
    return d.missingVar.join(', ');
  }
  if (d.type === 'CANNOT_ASSIGN_SUPPORTING_ACTOR') {
    return d.timelineItem;
  }
  if (d.type === 'CC_SEND_ERROR') {
    return d.itemTplId;
  }
  if (d.type === 'ASSIGNEE_BOUNCE') {
    return d.itemTplId;
  }
  return '';
};

export const IssueDetails = ({ d }: { d: ExperienceProblemData }) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const text = getIssueDetailsText(d, org.customUserFields, t);
  return text ? <LightMdToJsx>{text}</LightMdToJsx> : null;
};

export const getIssueTitleText = (
  d: ExperienceProblemData,
  t: UnsafeTFunction,
  participant?: Doc<IUser> | null
) => {
  if (d.type === 'AUTOMATION') {
    return d.automationTpl.title;
  }
  if (d.type === 'SENT_ITEM') {
    return d.humanReadableTitle;
  }
  if (d.type === 'MISSING_VAR') {
    return t('This instance has missing variables');
  }
  if (
    d.type === ErroDataType.CANNOT_ASSIGN_SUPPORTING_ACTOR ||
    d.type === ErroDataType.CANNOT_SEND_DOCUSIGN_ENVELOPE ||
    d.type === ErroDataType.CANNOT_SEND_BACKGROUND_CHECK
  ) {
    return d.timelineItem;
  }
  if (d.type === ErroDataType.SALESFORCE_CONTACT_RESOLUTION_FAILED) {
    // Participant upsert is per-instance (fires at experience launch), not
    // per-task — there's no task to surface. Use the participant name so
    // admins can scan rows distinctly when multiple participants fail to
    // upsert. Falls back to "Salesforce participant upsert" when participant
    // data isn't threaded through (e.g., the tracking-log call site).
    const userName = participant ? toFullName(participant.data) : null;
    return userName
      ? t('Failure creating Salesforce user: {{userName}}', { userName })
      : t('Salesforce participant upsert');
  }
  if (d.type === ErroDataType.SALESFORCE_WRITE_FAILED) {
    // Subtask-scoped SF write — d.timelineItem is populated by the collector
    // (collectInstanceIssues finds the parent task by subtaskTplId). Fall
    // back to a generic label if the resolver couldn't find the task.
    return (
      (d as { timelineItem?: string }).timelineItem ||
      t('Salesforce subtask write')
    );
  }
  if (d.type === 'CC_SEND_ERROR') {
    const expTitle = d.tpl.data.title;
    if (d.itemType === 'MESSAGE') {
      const msgTpl = getMessageTemplateById(d.tpl, d.itemTplId);
      return msgTpl ? resolveExpTitle(getItemDisplayName(msgTpl), expTitle) : '';
    }
    const taskTpl = getTaskTemplateById(d.tpl, d.itemTplId);
    return taskTpl ? resolveExpTitle(getItemDisplayName(taskTpl), expTitle) : '';
  }
  if (d.type === 'ASSIGNEE_BOUNCE') {
    const expTitle = d.tpl.data.title;
    if (d.itemType === 'MESSAGE') {
      // Interpolate the experience title into the item name (SC-33594) so the
      // row reads "[Acme Experience] …" — not the raw "[[ experience.title ]] …"
      // template token. Mirrors the CC_SEND_ERROR / SENT_ITEM title logic.
      const msgTpl = getMessageTemplateById(d.tpl, d.itemTplId);
      return msgTpl ? resolveExpTitle(getItemDisplayName(msgTpl), expTitle) : '';
    }
    // For TASK bounces, `itemTplId` is a task INSTANCE id (see the retry
    // handler in Details/index.tsx for the BE-quirk explanation). Map to
    // the template via the concrete task on the instance.
    const task = d.instance.data.tasks.find((t) => t.id === d.itemTplId);
    if (!task) return '';
    const taskTpl = getTaskTemplateById(d.tpl, task.templateId);
    return taskTpl ? resolveExpTitle(getItemDisplayName(taskTpl), expTitle) : '';
  }
  return '';
};

export const IssueTitle = ({
  d,
  participant
}: {
  d: ExperienceProblemData;
  participant?: Doc<IUser> | null;
}) => {
  const { t } = useUnsafeTranslation();
  const text = getIssueTitleText(d, t, participant);
  return text ? <strong>{text}</strong> : null;
};

export const UserTaskTimelineContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(3)
}));

const LaunchSuggestionContainer = styled(InfoBox)((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const LaunchSuggestionText = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const LaunchSuggestionActions = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

export const LaunchSuggestion = ({
  d,
  user
}: {
  d: Doc<IExperience>;
  user: Doc<IUser>;
}) => {
  const { t } = useUnsafeTranslation();
  const [startDay, setStartDay] = useState(toMoment(d.data.startsAt));
  return (
    <LaunchSuggestionContainer>
      <LaunchSuggestionText>
        {t(
          `This experience has been suggested by recent changes to {{ name }}'s profile. It has not been launched yet.`,
          {
            name: user.data.preferredName
          }
        )}
      </LaunchSuggestionText>
      <LaunchSuggestionActions>
        <div>{t('Start experience on')}</div>
        <DatePicker
          autoOk
          openTo="date"
          inputVariant="outlined"
          value={startDay}
          clearable={false}
          onChange={(m) => setStartDay(m! as any)}
        />
        <ButtonWithPromise
          variant="contained"
          color="primary"
          onClick={async () => {
            const org = await getOrg(d.data.orgId);
            if (!org) {
              console.error('org not found');
              return null;
            }
            return updateDoc(d, () => ({
              startsAt: fromMoment(
                startDay
                  .clone()
                  .tz(org.data.tz)
                  .hour(org.data.defaultExperienceStartHour)
                  .startOf('h')
              ),
              status: ExperienceStatus.NOT_STARTED
            }));
          }}
          pending={`${t('Launching')}...`}
        >
          {t('Launch')}
        </ButtonWithPromise>
      </LaunchSuggestionActions>
    </LaunchSuggestionContainer>
  );
};

const ManualReminderInfoBoxContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

export const SendManualReminder = ({
  d,
  user
}: {
  d: Doc<IExperience>;
  user: Doc<IUser>;
}) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const sendReminder = async () => {
    try {
      await sendManualReminder(d, PARTICIPANT_ACTOR_KEY);
      enqueueSnackbarSuccess('Reminder sent!');
    } catch (err: any) {
      if (err?.message === 'NO_LATE_TASKS') {
        enqueueSnackbarError(
          t('Cannot send reminder - there are no late tasks.')
        );
      } else {
        enqueueSnackbarError(t('Something went wrong. Please try again.'));
      }
    }
  };
  return (
    <ManualReminderInfoBoxContainer>
      <InfoBoxWithAction>
        <div>
          {t(`{{ name }} has not completed their tasks in time.`, {
            name: user.data.preferredName
          })}
        </div>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending={`${t('Sending')}...`}
          onClick={sendReminder}
        >
          {t('Send reminder')}
        </ButtonWithPromise>
      </InfoBoxWithAction>
    </ManualReminderInfoBoxContainer>
  );
};

const ViewTaskPageButtons = ({
  d,
  actorDescriptors,
  v = 1
}: {
  d: Doc<IExperience>;
  actorDescriptors: { [key: string]: ActorDescriptor };
  v?: 1 | 2;
}) => {
  const { t } = useUnsafeTranslation();
  const groupRef = useRef(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const otherActors = useMemo(() => {
    const res: {
      key: string;
      label: string;
    }[] = [];
    Object.entries(actorDescriptors)
      .filter(([k, sa]) => sa.type !== SupportingActorType.CHANNEL)
      .forEach(([key, desc]) => {
        if (key !== PARTICIPANT_ACTOR_KEY) {
          res.push({
            key,
            label: desc.label
          });
        }
      });
    return res;
  }, [actorDescriptors]);

  const mainButton = (
    <Button
      variant="outlined"
      endIcon={<ExternalLink size={16} />}
      target="_blank"
      rel="noopener"
      href={toStandaloneAppUrl(d, PARTICIPANT_ACTOR_KEY)}
    >
      {t('View {{ role }} task page', {
        role: actorDescriptors[PARTICIPANT_ACTOR_KEY]?.label || ''
      })}
    </Button>
  );
  return otherActors.length ? (
    <>
      <ButtonGroup variant="outlined" ref={groupRef}>
        {mainButton}
        <Button
          size="small"
          onClick={() => setPopperOpen((x) => !x)}
          aria-controls={popperOpen ? 'split-button-menu' : undefined}
          aria-expanded={popperOpen ? 'true' : undefined}
          aria-label={t('View task page')}
          aria-haspopup="menu"
        >
          <ChevronDown size={20} />
        </Button>
      </ButtonGroup>
      <Popper
        open={popperOpen}
        anchorEl={groupRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 2 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
                <MenuList id="split-button-menu">
                  {otherActors.map((option) => (
                    <MenuItem key={option.key}>
                      <LinkExternal
                        onClick={() => setPopperOpen(false)}
                        href={toStandaloneAppUrl(
                          d,
                          option.key,
                          EmployeeAppRerouterHints.FILTER_ALL
                        )}
                      >
                        {t('View {{ role }} task page', {
                          role: option.label
                        })}
                      </LinkExternal>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  ) : (
    mainButton
  );
};

type ConfirmationDialogProps = {};

// TODO - deal with Docusign tasks that are moved to the future to potentially
// void envelopes and allow for re-sending when the task restart
// See: https://app.shortcut.com/emplexp/story/12983/enable-people-to-run-docusign-tasks-again#activity-13199
const USE_CONFIRMATION_DIALOG = false;
const getPotentialConfirmationDialogProps = async (
  prev: Doc<IExperience>,
  next: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>
): Promise<ConfirmationDialogProps | null> => {
  if (!USE_CONFIRMATION_DIALOG) {
    return null;
  }
  // get all not completed docusign subtasks
  // check if they were either in running or done state before
  // if they were check if we have envelopes for them
  // if yes - ask for removal
  // or ask for save without removal
  const futureTasksWithOpenDocusignSubTasks = next.data.tasks.filter((t) => {
    return (
      t.subTasks.find(
        (s) =>
          s.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN &&
          s.status === SubTaskStatus.OPEN
      ) && tsIsAfter(t.timing.start, now())
    );
  });
  const tasksWhereDocuSignSubTaskStatusChanged = futureTasksWithOpenDocusignSubTasks.filter(
    (t) => {
      const otherTask = prev.data.tasks.find((ot) => ot.id === t.id);
      if (otherTask && tsIsAfter(now(), otherTask.timing.start)) {
        // Simpler than initially intended, i think this should be enough?
        return true;
      }
      return false;
    }
  );

  console.log(tasksWhereDocuSignSubTaskStatusChanged);

  if (tasksWhereDocuSignSubTaskStatusChanged.length) {
    const listeners = await getDocuSignWebhookListenerByInstance(
      next.data.orgId,
      next.id
    );
    console.log(listeners);
    if (listeners.length) {
      // there should only ever be one per resource...
      const listenersByResourceId = groupBy(
        listeners,
        (l) => l.data.resourceId
      );
    }
  }

  // now check if we have envelopes sent for these already
  // if yes - tell people that those can be resent.
  return {};
};

const PreSaveConfirmationDialog = ({
  data,
  onClose
}: {
  data: ConfirmationDialogProps;
  tpl: Doc<IExperienceTemplate>;
  onClose: () => void;
}) => {
  return null;
};

export const ParticipantDialog = ({
  d: originalD,
  tpl,
  open,
  onClose
}: {
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  open: boolean;
  onClose: () => void;
}) => {
  const { org, id: currentUserId } = useCurrentUser();
  const { customUserFields } = org;
  const newUxEnabled = useNewUx().enabled;
  const anchorFields = useAnchorFields(customUserFields);
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const { trackEvent } = useProductAnalytics();
  const [
    confirmationDialog,
    setConfirmationDialog
  ] = useState<ConfirmationDialogProps | null>(null);

  const [d, setD] = useState(originalD);
  useEffect(() => setD(originalD), [originalD]);

  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );

  const interpolationOptions: InterpolationDefaultOptions = useMemo(
    () => ({
      dateFormat: org.dateFormats.MMDDYYYY
    }),
    [org.dateFormats.MMDDYYYY]
  );

  const mapFn = useCallback(
    ([participant, resources, actors]: [
      Doc<IUser> | null,
      DenormalizationResources,
      { [actorKey: string]: Doc<IUser> }
    ]) => {
      return _mapFn(
        tpl,
        d,
        participant,
        resources,
        actors,
        customUserFields,
        org,
        actorDescriptors,
        interpolationOptions
      );
    },
    [d, tpl, customUserFields, org, actorDescriptors, interpolationOptions]
  );
  const participantActor = getParticipantActor(d);
  const [liveViewModel, loading, error] = useMappedLoadingValue(
    combineLoadingValues3(
      useUserById(participantActor?.userId || ''), // will always be defined
      useAdditionalDenormalizationResources(d),
      useActorsForExperience(d)
    ),
    mapFn,
    true
  );
  // useActorsForExperience briefly re-loads (liveViewModel momentarily undefined) whenever a
  // previously-unassigned supporting actor gets its first user, since that user's doc
  // wasn't subscribed to before. Every render below is gated on `viewModel`, so without this,
  // that flicker unmounts and remounts the whole dialog — resetting section/tab/scroll
  // state, which reads as the dialog reloading. Keep showing the last-loaded value
  // through that flicker instead; a real first load still falls through to the Loader.
  const lastViewModelRef = useRef(liveViewModel);
  const viewModel = advanceStickyValue(lastViewModelRef, liveViewModel);

  const [tab, setTab] = useState(0);
  const { t } = useUnsafeTranslation();

  const [selections, setSelections] = useState<{
    tasks: { [taskTplId: string]: boolean };
    messages: { [msgTplId: string]: boolean };
    automations: { [aTplId: string]: boolean };
  }>({ tasks: {}, automations: {}, messages: {} });

  const selectionCount = useMemo(() => {
    return (
      Object.values(selections.messages).filter((t) => t).length +
      Object.values(selections.tasks).filter((t) => t).length +
      Object.values(selections.automations).filter((t) => t).length
    );
  }, [selections]);

  // Check if instance is archived (either manually or auto-archived past threshold)
  const expectedEndThreshold = tpl.data.expectedEndThreshold ?? ONE_DAY * 14;

  // Matches UserTaskTimeline's own `expectedEnd` calc exactly — the actual rendered
  // width of the timeline (including any trailing empty weeks past the last real item,
  // out to the instance's expected/auto-archive end) rather than getTemplateDuration's
  // nominal template-design length, which can understate it when an instance's real
  // content finishes early but its rendered timeline still runs on for weeks after.
  const duration =
    d.data.expectedEnd.toMillis() - d.data.startsAt.toMillis() + expectedEndThreshold;
  const hasZoomControls = duration > ONE_DAY * 21;

  const discardDialog = useDialogState(false);
  const archiveDialog = useDialogState(false);
  const deleteDialog = useDialogState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteOtherReason, setDeleteOtherReason] = useState('');
  const isManuallyArchived = !!d.data.manuallyArchivedAt;
  const isAutoArchived = isInstanceAutoArchived(d.data, expectedEndThreshold);
  const isArchived = isManuallyArchived || isAutoArchived;

  const DELETION_REASONS = [
    { value: 'Duplicate - user enrolled more than once', label: t('Duplicate - user enrolled more than once') },
    { value: 'Need to restart the experience for this user', label: t('Need to restart the experience for this user') },
    { value: 'User no longer with the organization', label: t('User no longer with the organization') },
    { value: 'Incorrectly enrolled in the first place', label: t('Incorrectly enrolled in the first place') },
    { value: 'Test enrollment', label: t('Test enrollment') },
    { value: 'Other', label: t('Other') }
  ];

  const getFullDeleteReason = () => {
    if (deleteReason === 'Other') {
      return `Other: ${deleteOtherReason}`;
    }
    return deleteReason;
  };

  const isDeleteReasonValid = () => {
    if (!deleteReason) return false;
    if (deleteReason === 'Other') {
      return deleteOtherReason.trim().length >= 3;
    }
    return true;
  };

  const handleArchive = async () => {
    try {
      await archiveInstance(d.id);
      trackEvent({
        eventType: PAEventType.EXPERIENCE_INSTANCE,
        'Instance ID': d.id,
        name: 'Instance Archived',
        Explanation: 'Admin archives an experience instance'
      });
      enqueueSnackbarSuccess(t('Experience archived'));
      onClose();
    } catch (err) {
      captureException(err, {
        tags: { action: 'archive_instance' },
        extra: { instanceId: d.id, templateId: d.data.templateId }
      });
      enqueueSnackbarError(t('Failed to archive experience'));
      throw err;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteArchivedInstance(d.id, getFullDeleteReason());
      // Use PAEventType.OTHER to skip instance lookup (instance is already deleted)
      // Include enrichment data directly since we have it available
      trackEvent({
        eventType: PAEventType.OTHER,
        name: 'Instance Deleted',
        Explanation: 'Admin user deletes an instance',
        Subdomain: window.location.host,
        'Experience Template ID': d.data.templateId,
        'Experience Template Name': tpl.data.adminTitle || tpl.data.title,
        'Instance ID': d.id,
        'Instance Start Date': d.data.startsAt.toDate().toISOString(),
        'Instance Status': d.data.status,
        'Deletion Reason': getFullDeleteReason()
      } as any);
      enqueueSnackbarSuccess(t('Experience deleted'));
      onClose();
    } catch (err) {
      captureException(err, {
        tags: { action: 'delete_instance' },
        extra: { instanceId: d.id, templateId: d.data.templateId, reason: getFullDeleteReason() }
      });
      enqueueSnackbarError(t('Failed to delete experience'));
      throw err;
    }
  };

  const tryClose = () => {
    if (!isEqual(d, originalD)) {
      discardDialog.openDialog();
      return;
    }
    onClose();
  };
  // TODO say something when user doesn't exist anymore

  // TODO - if user is late, mention when the next reminder is going to be sent and allow
  // to send out a manual reminder.
  // A manual reminder is version of the LATE_REMINDER - need to see whether it can be safely reused.

  if (CA_TEST && viewModel) {
    console.log('EMAIL', viewModel.user.data.email);
    console.log('CAMP_GUIDE', viewModel.user.data.customFields.campGuide);
    console.log('ERGS', viewModel.user.data.customFields.erg);
  }

  const saveInstance = async (toSave: Doc<IExperience>) => {
    // make this a fine granular transactional update, so that there's never a weird situation with overwriting
    // a task status
    try {
      const saveTime = now();
      const fieldOverrides = getFieldOverridesFromChanges(
        tpl,
        originalD,
        toSave,
        currentUserId,
        saveTime
      );
      const withOverrides = updateDataInDoc(toSave, { fieldOverrides });
      const changeEvents = getChangeEvents(
        tpl,
        originalD,
        withOverrides,
        currentUserId,
        saveTime
      );
      const withEvents = updateDataInDoc(withOverrides, {
        events: [...withOverrides.data.events, ...changeEvents]
      });
      await setDoc(withEvents);
      await updateInstanceSchedules(withEvents.id);
      enqueueSnackbarSuccess(t('Changes saved!'));
    } catch (err: any) {
      console.error(err);
      enqueueSnackbarError(
        t('Failed to apply changes. Please try again or contact support.')
      );
    }
  };

  // employeeApp is absent on some templates (Sentry DADO-APP-33 crashed
  // ParticipantDialog at CharlieHealth). Optional-chain — the only use is the
  // `employeeAppV === 1.5` check below, which is correctly false when undefined.
  const employeeAppV = tpl.data.employeeApp?.v;

  return (
    <Dialog open={open} onClose={tryClose} fullScreen>
      {viewModel && !newUxEnabled && (
        <DialogActionsHeader
          onClose={tryClose}
          left={
            <WithShape
              shape="square"
              color={actorDescriptors[PARTICIPANT_ACTOR_KEY].color}
              wrap
            >
              <FlexContainer alignItems="center">
                <Typography variant="h4">
                  {toFullName(viewModel.user.data) ||
                    viewModel.user.data.email ||
                    viewModel.user.data.personalEmail}
                </Typography>
                {isArchived ? (
                  <ColoredChipDark color="gray" size="small">
                    {t('Archived')}
                  </ColoredChipDark>
                ) : (
                  <InstanceStatusChip status={d.data.status} size="small" />
                )}
              </FlexContainer>
            </WithShape>
          }
        >
          <ViewTaskPageButtons d={d} actorDescriptors={actorDescriptors} />
        </DialogActionsHeader>
      )}
      {viewModel && (
        <>
          {/*
            Rendered unconditionally (outside the classic/new-UX branch below) so both
            headers can trigger the same archive/delete/discard flow via the same
            archiveDialog/deleteDialog/discardDialog state, without duplicating this JSX.
          */}
          <DiscardChangesDialog
            open={discardDialog.dialogOpen}
            onCancel={discardDialog.closeDialog}
            onConfirm={onClose}
          />
          <Dialog
            open={archiveDialog.dialogOpen}
            onClose={archiveDialog.closeDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogContent>
              <Typography variant="h4" style={{ marginBottom: 24 }}>
                {t("Archive {{userName}}'s experience?", {
                  userName: viewModel ? toFullName(viewModel.user.data) || viewModel.user.data.email || 'this user' : 'this user'
                })}
              </Typography>
              <Typography>
                {t(
                  "Archiving will cancel all upcoming notifications and skip all remaining tasks/messages/automations in their experience. Nothing else will be sent in future for {{userName}}'s experience.",
                  {
                    userName: viewModel ? toFullName(viewModel.user.data) || viewModel.user.data.email || 'this user' : 'this user'
                  }
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={archiveDialog.closeDialog}>
                {t('Cancel')}
              </Button>
              <ButtonWithPromise
                variant="contained"
                color="primary"
                pending={`${t('Archiving')}...`}
                onClick={handleArchive}
              >
                {t('Archive experience')}
              </ButtonWithPromise>
            </DialogActions>
          </Dialog>
          <Dialog
            open={deleteDialog.dialogOpen}
            onClose={deleteDialog.closeDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogContent>
              <Typography variant="h4" style={{ marginBottom: 24 }}>
                {t("Delete {{userName}}'s experience?", {
                  userName: viewModel ? toFullName(viewModel.user.data) || viewModel.user.data.email || 'this user' : 'this user'
                })}
              </Typography>
              <InfoBox color="red" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography variant="body2">
                  {deleteReason
                    ? t('Be careful! This action cannot be undone.')
                    : t('Be careful! Deleting cannot be undone, and all data about this experience will be permanently lost.')}
                </Typography>
              </InfoBox>
              <Typography style={{ marginBottom: 16 }}>
                {deleteReason
                  ? t(
                      "This will permanently remove {{userName}} from the {{experienceName}} experience, and delete all records of what's been sent and which tasks have been completed.",
                      {
                        userName: viewModel ? toFullName(viewModel.user.data) || viewModel.user.data.email || 'this user' : 'this user',
                        experienceName: tpl.data.title
                      }
                    )
                  : t(
                      "This will delete all records of what's been sent and which tasks have been completed. We recommend archiving rather than deleting so the reporting about your experiences remains complete and accurate."
                    )}
              </Typography>
              <Typography
                variant="body2"
                style={{ marginBottom: 8, fontWeight: 500 }}
              >
                {t('Select a deletion reason')}
              </Typography>
              <FormControl fullWidth variant="outlined">
                <Select
                  value={deleteReason}
                  onChange={(e) => {
                    setDeleteReason(e.target.value as string);
                    if (e.target.value !== 'Other') {
                      setDeleteOtherReason('');
                    }
                  }}
                >
                  {DELETION_REASONS.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {deleteReason === 'Other' && (
                <>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}
                  >
                    {t('Please specify')}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={deleteOtherReason}
                    onChange={(e) => setDeleteOtherReason(e.target.value)}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={deleteDialog.closeDialog}>
                {t('Cancel')}
              </Button>
              <ButtonWithPromise
                variant="contained"
                color="secondary"
                pending={`${t('Deleting')}...`}
                onClick={handleDelete}
                disabled={!isDeleteReasonValid()}
              >
                {t('Delete experience')}
              </ButtonWithPromise>
            </DialogActions>
          </Dialog>
        </>
      )}
      <DialogContent
        style={
          newUxEnabled
            ? {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                // MUI's DialogContent class defaults to overflow-y: auto. Body (inside
                // ParticipantDialogNewUx) is the only scrolling ancestor the new-UX tree
                // is built against — position: sticky elements inside it are positioned
                // relative to Body, so a second active scroll container here would break
                // them. Keep this overflow: hidden for as long as that's true.
                overflow: 'hidden'
              }
            : undefined
        }
      >
        {loading && !viewModel && <Loader height={500} />}
        {error && <SomethingWentWrong height={300} />}
        {viewModel && newUxEnabled && (
          <ParticipantDialogNewUx
            d={d}
            setD={setD}
            originalD={originalD}
            tpl={tpl}
            org={org}
            customUserFields={customUserFields}
            anchorFields={anchorFields}
            hasZoomControls={hasZoomControls}
            selections={selections}
            setSelections={setSelections}
            v={viewModel}
            isArchived={isArchived}
            actorDescriptors={actorDescriptors}
            onOpenArchiveDialog={archiveDialog.openDialog}
            onOpenDeleteDialog={deleteDialog.openDialog}
            saveInstance={saveInstance}
            onClose={tryClose}
          />
        )}
        {viewModel && !newUxEnabled && (
          <>
            {d.data.status === ExperienceStatus.SUGGESTED && (
              <LaunchSuggestion d={d} user={viewModel.user} />
            )}
            <UserTaskTimelineContainer>
              <UserTaskTimeline
                d={d}
                tpl={tpl}
                variant="normal"
                onChange={setD}
                anchorFields={anchorFields}
                selections={selections}
                setSelections={setSelections}
                hasZoomControls={hasZoomControls}
              />
            </UserTaskTimelineContainer>
            {d.data.status === ExperienceStatus.LATE &&
              USE_MANUAL_REMINDERS && (
                <SendManualReminder d={d} user={viewModel.user} />
              )}
            <Tabs
              value={tab}
              onChange={(ev, i) => setTab(i)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value={0} label={t('Experience Status')} wrapped />
              <Tab value={1} label={t('Experience Log')} wrapped />
              <Tab value={2} label={t('Pending Schedules')} wrapped />
            </Tabs>
            <TabPanel active={tab === 0}>
              <TimelineItemStatusList
                tpl={tpl}
                originalD={originalD}
                d={d}
                setD={setD}
                selections={selections}
                setSelections={setSelections}
                timelineItems={viewModel.items}
                replacements={viewModel.replacements}
                onRestartDocusignSubTask={async (
                  trigger,
                  items,
                  voidedReason
                ) => {
                  const nextD = items.reduce((nd, x) => {
                    const tsk = d.data.tasks.find(
                      (tk) => tk.templateId === x.taskTplId
                    );
                    if (tsk) {
                      const subTask = tsk.subTasks.find(
                        (s) => s.templateId === x.subTaskTplId
                      );
                      if (subTask) {
                        return openSubTask(tpl, nd, tsk.id, subTask.id);
                      }
                    }
                    return nd;
                  }, d);

                  nextD.data.events.push({
                    id: shortid(),
                    type: ExperienceEventType.DOCUSIGN_RESTARTED,
                    resourceId: trigger.resourceId,
                    at: now(),
                    channel: EventChannel.ADMIN_APP,
                    initiator: currentUserId
                  });

                  setD(nextD);
                  await Promise.all([
                    saveInstance(nextD),
                    // We only need to trigger one request per resource - all other items will be dealt with on the other end
                    callFirebaseFunction(
                      'integrations_docusign-voidExistingAndMaybeCreateNewEnvelope',
                      {
                        orgId: tpl.data.orgId,

                        tplId: tpl.id,
                        expId: nextD.id,
                        subTaskTemplateId: trigger.subTaskTplId,
                        taskTemplateId: trigger.taskTplId,
                        resourceId: trigger.resourceId,
                        voidedReason,
                        createNewEnvelope: true
                      }
                    )
                  ]);
                }}
              />
            </TabPanel>
            <TabPanel active={tab === 1}>
              <ExperienceLog
                org={org}
                tpl={tpl}
                d={d}
                tasks={viewModel.tasks}
                messages={viewModel.messages}
                automations={viewModel.automations}
                anchorFields={anchorFields}
              />
            </TabPanel>
            <TabPanel active={tab === 2}>
              <UpcomingSchedules tpl={tpl} exp={d} />
            </TabPanel>
          </>
        )}
      </DialogContent>
      {!newUxEnabled && (selectionCount > 0 ? (
        <DialogActionsWithSlots
          left={
            <>
              <Typography variant="body1">
                {selectionCount === 1
                  ? t('1 item selected')
                  : t('{{ count }} items selected', { count: selectionCount })}
              </Typography>
              <Button
                color="primary"
                onClick={() => {
                  setSelections({ tasks: {}, automations: {}, messages: {} });
                }}
              >
                {t('Deselect all')}
              </Button>
            </>
          }
          right={
            <CustomizeDates
              tpl={tpl}
              exp={d}
              selections={selections}
              onChange={(nextD) => {
                setD(nextD);
                setSelections({ tasks: {}, automations: {}, messages: {} });
              }}
            />
          }
        />
      ) : (
        <DialogActionsWithSlots
          left={
            // Show Delete button if instance is archived (manually or auto-archived past threshold)
            // Show Archive button if instance is still active
            isArchived ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={deleteDialog.openDialog}
              >
                {t('Delete experience')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={archiveDialog.openDialog}
              >
                {t('Archive experience')}
              </Button>
            )
          }
          right={
            <>
              <Button color="primary" onClick={tryClose}>
                {isEqual(d, originalD) ? t('Cancel') : t('Discard changes')}
              </Button>
              <ButtonWithPromise
                color="primary"
                variant="contained"
                pending={`${t('Saving')}...`}
                onClick={async () => {
                  const confirmationDialogProps = await getPotentialConfirmationDialogProps(
                    originalD,
                    d,
                    tpl
                  );
                  if (confirmationDialogProps) {
                    setConfirmationDialog(confirmationDialogProps);
                  } else {
                    await saveInstance(d);
                  }
                }}
              >
                {t('Save changes')}
              </ButtonWithPromise>

              {confirmationDialog && (
                <PreSaveConfirmationDialog
                  data={confirmationDialog}
                  tpl={tpl}
                  onClose={() => setConfirmationDialog(null)}
                />
              )}
            </>
          }
        />
      ))}
    </Dialog>
  );
};

export const ParticipantDialogLazy = ({
  instanceId,
  tplId,
  open,
  onClose
}: {
  instanceId: string;
  tplId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const [v, , error] = useCombineLoadingValues(
    useExperienceTemplateById(tplId),
    useExperienceInstanceById(instanceId)
  );

  if (error) {
    onClose();
    return null;
  }

  // Opening a participant for the first time is a cold Firestore listen (the V2
  // list holds compact pseudo-instances, so the full instance is re-fetched by
  // id here — cache miss + cold realtime channel on the first click). Until it
  // resolves `v` is undefined; render the open dialog shell with a Loader so the
  // click has immediate visible feedback instead of nothing appearing to happen.
  // Subsequent opens hit the module-level listener cache and resolve instantly.
  if (!v) {
    return (
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogActionsHeader onClose={onClose} />
        <Loader height={500} />
      </Dialog>
    );
  }
  const [tpl, inst] = v;

  if (!tpl || !inst) {
    onClose();
    // show a snack?
    return null;
  }

  return <ParticipantDialog open={open} onClose={onClose} tpl={tpl} d={inst} />;
};

/*

OZ5cbmSiOc_

WOOKBYCP-


*/
